import { Box, Button, Grid, Typography } from "@mui/material";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { useContext, useState } from "react";

import { OutreachAutomationType } from "../../../types";

const SOURCE_OPTIONS: {
  type: OutreachAutomationType;
  icon: string;
  iconBg: string;
  iconColor?: string;
  title: string;
  description: string;
  volume: string;
}[] = [
  {
    type: "bento_database",
    icon: "fa-solid fa-database",
    iconBg: "purple.main",
    iconColor: "#3b0764",
    title: "Bento Brand Database",
    description:
      "Discover brands from our curated database matched to your niche and preferences.",
    volume: "~10-50 brands/week",
  },
  {
    type: "instagram_tracked_creators",
    icon: "fa-brands fa-instagram",
    iconBg: "yellow.main",
    iconColor: "#92400e",
    title: "Instagram Creator",
    description:
      "Find brands that are already working with creators similar to you on Instagram.",
    volume: "~2-8 brands/week",
  },
  {
    type: "google_places",
    icon: "fa-solid fa-location-dot",
    iconBg: "#00a63e",
    iconColor: "#14532d",
    title: "Local Business Search",
    description:
      "Discover local businesses near you that could be great brand partners.",
    volume: "~15-40 brands/week",
  },
];

interface SourceSelectionProps {
  onNext: (type: OutreachAutomationType) => void;
  initialType?: OutreachAutomationType | null;
}

export default function SourceSelection({
  onNext,
  initialType,
}: SourceSelectionProps) {
  const { discoverTab } = useContext(DiscoverViewContext);
  const sidebarOpen = discoverTab !== null;
  const [selectedType, setSelectedType] =
    useState<OutreachAutomationType | null>(initialType ?? null);

  return (
    <Box sx={{ maxWidth: 960, mx: "auto", mt: 4 }}>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: 22,
          color: "primary.main",
          textAlign: "center",
          mb: 1,
        }}
      >
        Choose Your Brand Source
      </Typography>
      <Typography
        sx={{
          fontSize: 14,
          color: "rgba(1,67,57,0.6)",
          textAlign: "center",
          mb: 4,
        }}
      >
        Select where you want to discover brands from
      </Typography>

      <Grid container spacing={2}>
        {SOURCE_OPTIONS.map((option) => {
          const isSelected = selectedType === option.type;
          return (
            <Grid
              item
              xs={12}
              sm={sidebarOpen ? 12 : 6}
              md={sidebarOpen ? 6 : 4}
              key={option.type}
            >
              <Box
                onClick={() => setSelectedType(option.type)}
                sx={{
                  backgroundColor: isSelected ? "rgba(1,67,57,0.03)" : "white",
                  border: "2px solid",
                  borderColor: isSelected
                    ? "primary.main"
                    : "rgba(1,67,57,0.1)",
                  borderRadius: "14px",
                  p: 3,
                  height: "100%",
                  cursor: "pointer",
                  transition: "all 0.15s ease",
                  "&:hover": {
                    borderColor: "primary.main",
                    boxShadow: "0 4px 12px rgba(1,67,57,0.1)",
                  },
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: "10px",
                    backgroundColor: option.iconBg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1.5,
                  }}
                >
                  <Box
                    component="span"
                    className={option.icon}
                    sx={{ fontSize: 18, color: option.iconColor ?? "white" }}
                  />
                </Box>
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: 16,
                    color: "primary.main",
                    mb: 0.5,
                  }}
                >
                  {option.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: "rgba(1,67,57,0.6)",
                    lineHeight: "20px",
                    mb: 1.5,
                  }}
                >
                  {option.description}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "rgba(1,67,57,0.5)",
                  }}
                >
                  {option.volume}
                </Typography>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      <Button
        variant="contained"
        fullWidth
        disabled={!selectedType}
        onClick={() => selectedType && onNext(selectedType)}
        sx={{
          mt: 4,
          maxWidth: 400,
          mx: "auto",
          display: "block",
          borderRadius: "10px",
          backgroundColor: "primary.main",
          fontWeight: 600,
          fontSize: 16,
          py: 1.5,
          textTransform: "none",
        }}
      >
        Next
      </Button>
    </Box>
  );
}
